/** Compact, navigable component context using batched tree and operational data. */
import { Link } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { RemoveDialog } from "@/features/assets/AssetActionDialogs";
import { AssetMeterReading } from "@/features/assets/AssetMeterReading";
import { AssetMeterSummary } from "@/features/assets/AssetMeterSummary";
import { AssetReference } from "@/features/entities/AssetReference";
import { type AssetTreeNode, type ComponentHealth, useAsset } from "@/features/assets/queries";
import { ErrorState, LoadingState, StatusBadge } from "@/shared/components/page";
import { formatDateTime, formatQuantity } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Dialog } from "@/shared/ui/primitives";

/** Load full detail only after the user requests removal, not for every component. */
function ComponentRemoval({ id, onClose }: { id: string; onClose: () => void }): ReactElement {
  const asset = useAsset(id);
  const { t } = useTranslation();
  if (asset.data) return <RemoveDialog asset={asset.data} open onOpenChange={onClose} />;
  return (
    <Dialog open onOpenChange={onClose} title={t("assets.remove")}>
      {asset.isError ? (
        <ErrorState error={asset.error} onRetry={() => void asset.refetch()} />
      ) : (
        <LoadingState />
      )}
    </Dialog>
  );
}

/** Named component with status, dated meters, maintenance context, and authorized actions. */
export function AssetSummaryCard({
  asset,
  health,
}: {
  asset: AssetTreeNode;
  health?: ComponentHealth;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [action, setAction] = useState<"reading" | "remove" | null>(null);
  return (
    <article className="min-w-0 rounded-md border p-3" aria-label={`${asset.code} · ${asset.name}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <AssetReference id={asset.id} code={asset.code} name={asset.name} />
        <StatusBadge value={asset.status} namespace="assets.status" />
      </div>
      <p className="text-sm text-muted-foreground">
        {asset.type_name}
        {asset.position ? ` · ${asset.position}` : ""}
      </p>
      <AssetMeterSummary meters={asset.meters} />
      {health ? (
        <div className="mt-2 space-y-1 text-sm">
          <p>
            {t("assets.openWorkOrders")}:{" "}
            {health.open_work_orders ?? t("assets.summary.unavailable")} · {t("requests.title")}:{" "}
            {health.open_faults ?? t("assets.summary.unavailable")}
          </p>
          <p>
            {t("assets.summary.restrictions")}:{" "}
            {health.restrictions ?? t("assets.summary.unavailable")}
          </p>
          {health.maintenance == null ? (
            <p>
              {t("maintenance.title")}: {t("assets.summary.unavailable")}
            </p>
          ) : health.maintenance.length === 0 ? (
            <p>{t("assets.summary.noMaintenance")}</p>
          ) : (
            health.maintenance.map((plan) => (
              <p key={plan.id}>
                {plan.name} · <StatusBadge value={plan.status} namespace="maintenance.status" />
                {plan.due_at ? ` · ${formatDateTime(plan.due_at)}` : ""}
                {plan.due_meter != null
                  ? ` · ${t("assets.summary.dueAt")} ${formatQuantity(plan.due_meter)} ${plan.meter_unit ?? ""}`
                  : ""}
              </p>
            ))
          )}
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {can("requests.request.create") ? (
          <Button asChild size="sm" variant="secondary">
            <Link to="/requests" search={{ asset_id: asset.id }}>
              {t("requests.new")}
            </Link>
          </Button>
        ) : null}
        <Button asChild size="sm" variant="outline">
          <Link to="/assets/$assetId" params={{ assetId: asset.id }}>
            {t("assets.summary.open")}
          </Link>
        </Button>
        <details className="max-w-full text-sm">
          <summary className="cursor-pointer rounded-md border px-3 py-2">
            {t("assets.summary.moreActions")}
          </summary>
          <div className="mt-2 flex flex-wrap gap-2">
            {can("meters.reading.record") && (asset.meters ?? []).length > 0 ? (
              <Button size="sm" variant="outline" onClick={() => setAction("reading")}>
                {t("assets.recordReading")}
              </Button>
            ) : null}
            {can("workorders.workorder.create") ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/work-orders" search={{ asset_id: asset.id }}>
                  {t("workOrders.new")}
                </Link>
              </Button>
            ) : null}
            {can("assets.asset.install") ? (
              <Button
                size="sm"
                variant="outline"
                aria-label={`${t("assets.remove")}: ${asset.name}`}
                onClick={() => setAction("remove")}
              >
                {t("assets.remove")}
              </Button>
            ) : null}
            {can("workorders.workorder.view_all") || can("workorders.workorder.view_assigned") ? (
              <Button asChild size="sm" variant="outline">
                <Link
                  to="/assets/$assetId"
                  params={{ assetId: asset.id }}
                  search={{ tab: "history" }}
                >
                  {t("assets.maintenanceHistory")}
                </Link>
              </Button>
            ) : null}
          </div>
        </details>
      </div>
      {action === "reading" ? (
        <AssetMeterReading asset={asset} onClose={() => setAction(null)} />
      ) : null}
      {action === "remove" ? (
        <ComponentRemoval id={asset.id} onClose={() => setAction(null)} />
      ) : null}
    </article>
  );
}

/** Retain hierarchy at every depth without creating per-component API requests. */
export function ComponentTree({
  node,
  health,
  depth = 0,
}: {
  node: AssetTreeNode;
  health: ReadonlyMap<string, ComponentHealth>;
  depth?: number;
}): ReactElement {
  return (
    <li className="min-w-0 space-y-2">
      <AssetSummaryCard asset={node} health={health.get(node.id)} />
      {(node.children ?? []).length > 0 ? (
        <ul className="space-y-2" style={{ paddingInlineStart: Math.min(depth + 1, 3) * 8 }}>
          {(node.children ?? []).map((child) => (
            <ComponentTree key={child.id} node={child} health={health} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
